import { StrictMode } from "react";
import { Provider } from "react-redux";
import { Portal, Toaster } from "saphir";

import { isAccessRedirectError } from "@lib/access/access";
import { runtimeEnvOr } from "@lib/env";
import trackEvent from "@lib/trackers";
import { setLanguageTag } from "@paraglide/runtime";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AppRouter } from "@routes";
import { isSpellbookError, ScopeContextChangedError } from "@sdk/spellbook";
import { captureException } from "@sentry/react";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { FlagProvider } from "@unleash/proxy-client-react";

import { handleApiError, reportLegacyApiCacheError } from "../instrument";

import Error from "./components/Error";

import "@styles/tailwind.css";

import "saphir/props";
import "sui/props";

const config = {
    url: runtimeEnvOr("REACT_APP_UNLEASH_URL", "REACT_APP_UNLEASH_URL") as string,
    clientKey: runtimeEnvOr(
        "REACT_APP_UNLEASH_CLIENT_KEY",
        "REACT_APP_UNLEASH_CLIENT_KEY",
    ) as string,
    refreshInterval: 15,
    appName: "team",
};
setLanguageTag((localStorage.getItem("side_team_locale") || "fr") as "en" | "fr");

function onCacheError(error: Error, source: "query" | "mutation") {
    if (isSpellbookError(error)) {
        reportLegacyApiCacheError(error);
        return;
    }

    // A few legacy access errors still come from adapters outside Spellbook.
    if (isAccessRedirectError(error)) {
        handleApiError(error, { source });
        return;
    }

    captureException(error);
    console.error(error);
}

const queryCache = new QueryCache({ onError: (error) => onCacheError(error, "query") });
const mutationCache = new MutationCache({ onError: (error) => onCacheError(error, "mutation") });

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 20,
            retry: (failureCount, error) =>
                !(error instanceof ScopeContextChangedError) &&
                !isAccessRedirectError(error) &&
                failureCount < 1,
        },
    },
    queryCache,
    mutationCache,
});

trackEvent({
    name: "team - get user locale",
    params: { locale: localStorage.getItem("side_team_locale") || "fr" },
});

const App = ({ store, errorDisplay = false }) => (
    <StrictMode>
        <Provider store={store}>
            <FlagProvider config={config}>
                <GoogleOAuthProvider clientId='54503600174-07prhmecenv6bic8im520a7uq1a9b8sb.apps.googleusercontent.com'>
                    <QueryClientProvider client={queryClient}>
                        <div className='app root isolate'>
                            {errorDisplay && <Error />}
                            <AppRouter />
                            <Portal>
                                <Toaster />
                            </Portal>
                        </div>
                        <ReactQueryDevtools initialIsOpen={false} />
                    </QueryClientProvider>
                </GoogleOAuthProvider>
            </FlagProvider>
        </Provider>
    </StrictMode>
);

export default App;
